import {
  ArrowLeft,
  CalendarDays,
  ClipboardCheck,
  Filter,
  Home,
  Plus,
  Settings,
  TrendingUp,
  WalletCards,
  Wrench,
  Zap,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import type { DashboardView } from '../../features/dashboard/dashboard.types';
import type { TranslationKey } from '../../i18n/messages';
import { useI18n } from '../../i18n/useI18n';

export type DockContext = 'main' | 'plan' | 'tools';
interface DockItem {
  readonly activeId?: string;
  readonly icon: LucideIcon;
  readonly label: TranslationKey;
  readonly onPress: () => void;
}

interface DockProps {
  readonly activeDestination: string;
  readonly context: DockContext;
  readonly onAdd: () => void;
  readonly onBack: () => void;
  readonly onFilter: () => void;
  readonly onNavigate: (view: DashboardView) => void;
  readonly onSettings: () => void;
  readonly onWallets: () => void;
  readonly showFilter: boolean;
}

export function Dock(props: DockProps) {
  const { t } = useI18n();
  const items = getDockItems(props);

  return (
    <nav
      className="mobile-dock"
      aria-label={t('navigation.main')}
      data-context={props.context}
      data-item-count={items.length}
    >
      {items.map(({ activeId, icon: Icon, label, onPress }) => {
        const isActive = activeId === props.activeDestination;
        return (
          <button
            aria-current={isActive ? 'page' : undefined}
            className={isActive ? 'dock-link is-active' : 'dock-link'}
            key={`${props.context}-${label}`}
            type="button"
            onClick={onPress}
          >
            <Icon aria-hidden="true" />
            <span>{t(label)}</span>
          </button>
        );
      })}
    </nav>
  );
}

function getDockItems(props: DockProps): readonly DockItem[] {
  if (props.context === 'plan') {
    return [
      { icon: ArrowLeft, label: 'common.back', onPress: props.onBack },
      {
        activeId: 'plan',
        icon: TrendingUp,
        label: 'navigation.plan',
        onPress: () => props.onNavigate('plan'),
      },
      {
        activeId: 'wallets',
        icon: WalletCards,
        label: 'navigation.wallets',
        onPress: props.onWallets,
      },
      { icon: Plus, label: 'navigation.add', onPress: props.onAdd },
      ...(props.showFilter
        ? [{ icon: Filter, label: 'navigation.filters', onPress: props.onFilter } as const]
        : []),
    ];
  }

  if (props.context === 'tools') {
    return [
      { icon: ArrowLeft, label: 'common.back', onPress: props.onBack },
      {
        activeId: 'agenda',
        icon: ClipboardCheck,
        label: 'navigation.agenda',
        onPress: () => props.onNavigate('agenda'),
      },
      {
        activeId: 'calendar',
        icon: CalendarDays,
        label: 'navigation.calendar',
        onPress: () => props.onNavigate('calendar'),
      },
      {
        activeId: 'tools',
        icon: Wrench,
        label: 'navigation.tools',
        onPress: () => props.onNavigate('tools'),
      },
      ...(props.showFilter
        ? [{ icon: Filter, label: 'navigation.filters', onPress: props.onFilter } as const]
        : []),
    ];
  }

  return [
    {
      activeId: 'home',
      icon: Home,
      label: 'navigation.home',
      onPress: () => props.onNavigate('home'),
    },
    {
      activeId: 'plan',
      icon: TrendingUp,
      label: 'navigation.plan',
      onPress: () => props.onNavigate('plan'),
    },
    {
      activeId: 'quick',
      icon: Zap,
      label: 'navigation.quick',
      onPress: () => props.onNavigate('quick'),
    },
    {
      activeId: 'tools',
      icon: Wrench,
      label: 'navigation.tools',
      onPress: () => props.onNavigate('tools'),
    },
    {
      activeId: 'settings',
      icon: Settings,
      label: 'navigation.settings',
      onPress: props.onSettings,
    },
  ];
}
